import { Crown, UserRound } from "lucide-react";
import { shortVoiceName } from "@/lib/studioApi";

function SourceButton({ active, onClick, title, body, testId, badge }) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-2xl border px-3.5 py-3 text-left transition-colors duration-200 ${active ? "border-amber-300/60 bg-amber-400/15 text-white" : "border-white/10 bg-black/30 text-white/70 hover:bg-white/[0.06]"}`}
    >
      <span className="flex items-center gap-2 font-display text-[13px] font-semibold">
        {title}
        {badge ? <span className="rounded-full border border-amber-300/40 px-1.5 py-px font-mono text-[9px] uppercase tracking-wider text-amber-200">{badge}</span> : null}
      </span>
      <span className="mt-0.5 block text-[11px] leading-snug text-white/50">{body}</span>
    </button>
  );
}

/** One signature voice profile; the selected one shows the AI / My Recorded Voice choice. */
export default function ProfileCard({ profile, selected, source, onSelect, onSource, children }) {
  const main = profile.is_main;
  const select = () => { if (!selected) onSelect(profile); };
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={main ? "vocal-signature" : `vocal-profile-${profile.id}`}
      data-profile-id={profile.id}
      aria-pressed={selected}
      onClick={select}
      onKeyDown={(e) => { if (e.key === "Enter") select(); }}
      className={`cursor-pointer rounded-3xl border transition-colors duration-200 ${main ? "p-5" : "p-4"} ${selected ? "border-amber-300/50 bg-gradient-to-br from-amber-500/[0.12] via-rose-500/[0.06] to-transparent" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"}`}
    >
      <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-amber-200/90">
        {main ? <Crown className="h-3.5 w-3.5" /> : <UserRound className="h-3.5 w-3.5" />} {main ? "Signature voice" : "Guest voice"}
        {profile.cloned_voice_id ? <span className="rounded-full border border-emerald-300/40 px-1.5 py-px text-[9px] text-emerald-200">Recorded voice linked</span> : null}
      </p>
      <p className={`mt-2 font-display font-bold tracking-wide ${main ? "text-base uppercase sm:text-lg" : "text-[15px]"}`}>{main ? profile.name : shortVoiceName(profile.name)}</p>
      <p className={`mt-1.5 text-[12px] leading-relaxed text-white/60 ${selected || main ? "" : "line-clamp-2"}`}>{profile.description || profile.style}</p>
      {selected ? (
        <div className="mt-4" onClick={(e) => e.stopPropagation()} role="presentation">
          <div className="grid gap-2 sm:grid-cols-2">
            <SourceButton testId="vocal-source-ai" active={source !== "my_voice"} onClick={() => onSource("ai")} title="AI Gospel Voice" body="ELPM sings in this voice's style." />
            <SourceButton testId="vocal-source-my-voice" active={source === "my_voice"} onClick={() => onSource("my_voice")} title="My Recorded Voice" badge="Experimental" body="Take 1 is re-sung in your authorised recorded voice." />
          </div>
          {children}
        </div>
      ) : null}
    </div>
  );
}
